import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import {
  useLogisticsPermissions,
  type LogisticsPermissions,
} from "../hooks/useLogisticsPermissions";
import { TreasuryAccount } from "../types/treasury.types";

const { Text } = Typography;
const { Option } = Select;

type LogisticsTreasuryTransfersProps = {
  onTransferCompleted?: () => void;
  permissions?: LogisticsPermissions;
  moduleScope?: "logistics" | "commercial" | "all";
};

const LogisticsTreasuryTransfers: React.FC<LogisticsTreasuryTransfersProps> = ({
  onTransferCompleted,
  permissions: permissionsOverride,
  moduleScope: moduleScopeOverride,
}) => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const fallbackPermissions = useLogisticsPermissions();
  const permissions = permissionsOverride ?? fallbackPermissions;
  const moduleScope = moduleScopeOverride ?? "logistics";
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchAccounts = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) {
      setAccounts([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/treasury/accounts`,
        {
          params: {
            company_id: selectedCompany,
            warehouse_id: selectedWarehouse,
            include_inactive: false,
            module_scope: moduleScope,
          },
        }
      );
      if (response.data?.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error("[TREASURY] Error fetching accounts for transfer:", error);
    }
  }, [selectedCompany, selectedWarehouse, moduleScope]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSubmit = async () => {
    if (!selectedCompany || !selectedWarehouse) {
      message.warning("Selectionnez une entreprise et un établissement.");
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        module_scope: moduleScope,
        from_account_id: values.from_account_id,
        to_account_id: values.to_account_id,
        amount: values.amount,
        movement_date: values.movement_date
          ? values.movement_date.format("YYYY-MM-DD")
          : undefined,
        reference: values.reference || undefined,
        description: values.description || undefined,
      };

      await axios.post(`${API_CONFIG.API_URL}/treasury/transfers`, payload);
      message.success("Transfert enregistre.");
      form.resetFields();
      form.setFieldsValue({ movement_date: dayjs() });
      fetchAccounts();
      onTransferCompleted?.();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error("[TREASURY] Error creating transfer:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de l'enregistrement du transfert"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!selectedCompany || !selectedWarehouse) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise et un établissement pour effectuer un transfert."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          Les transferts internes generent automatiquement deux mouvements
          (sortie et entree) dans la tresorerie.
        </Text>
        <Form form={form} layout="vertical" initialValues={{ movement_date: dayjs() }}>
          <Form.Item
            name="from_account_id"
            label="Compte source"
            rules={[{ required: true, message: "Compte source requis" }]}
          >
            <Select placeholder="Selectionner le compte source">
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="to_account_id"
            label="Compte destination"
            rules={[{ required: true, message: "Compte destination requis" }]}
          >
            <Select placeholder="Selectionner le compte destination">
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Montant"
            rules={[{ required: true, message: "Montant requis" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="movement_date" label="Date">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={!permissions.canCreateTreasuryTransfers}
          >
            Enregistrer le transfert
          </Button>
        </Form>
      </Space>
    </Card>
  );
};

export default LogisticsTreasuryTransfers;
